import {useState, useEffect} from "react";
import {useLocation} from "react-router-dom";
import {useLocale} from "../../shared-services/translate";
import {useSelector, useCartActions} from "../../shared-services/redux-tiny";
import {cartTools} from "./useCart";
import axios from "axios";
import apiEndpoints from "../shared/apiEndpoints";
import cartParser from "./cartParser";
import angularIntegration from "../../angular-integration";

const baseEndpoint = apiEndpoints.frontendOrders;
const updateInterval = 1500;

const useCartLifeCycle = () => {
  const [orderNumber, setOrderNumber] = useState(null);
  const [orderToken, setOrderToken] = useState(null);
  const [signalCartPush, setSignalCartPush] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const [apiCallController, setApiCallController] = useState(null);
  const parseUpdateLineItems = cartParser("cartUpdateItems");

  const {currentHub} = useSelector(state => state.session);
  const {loadRequested, loadOptions} = useSelector(state => state.cart);
  const {pageCode} = useSelector(state => state.router);

  const location = useLocation();

  const {setCurrentCart, signalCartIsLoading, signalCartWasLoaded} = useCartActions();
  const {$broadcast} = angularIntegration;

  const {getCurrentCart} = cartTools();

  const locale = useLocale();

  const cancelCurrentUpdateTimer = () => {
    if (window.cartTimer) {
      clearTimeout(window.cartTimer);
      window.cartTimer = null;
    }
  };

  const resetUpdateTimer = () => {
    cancelCurrentUpdateTimer();
    window.cartTimer = setTimeout(() => setSignalCartPush(true), updateInterval);
  };

  const initializeOrderParams = () => {
    const {currentOrderNumber, currentOrderToken} = window;

    if (currentOrderNumber && currentOrderToken) {
      setOrderNumber(window.currentOrderNumber);
      setOrderToken(window.currentOrderToken);
    } else {
      axios
        .get(`${baseEndpoint}/current.json`)
        .then(order => {
          if (order?.number) setOrderNumber(order.number);
          if (order?.token) setOrderToken(order.token);
        });
    }
  };

  const signalStartOfPushing = () => {
    setIsPushing(true);
    setSignalCartPush(false);
    $broadcast("reactCart:push:started");
  };

  const signalEndOfPushing = () => {
    setIsPushing(false);
    $broadcast("reactCart:push:finished");
  };

  const pushCartUpdates = () => {
    if (isLoading) {
      resetUpdateTimer();
      return;
    }

    if (apiCallController) apiCallController.abort();

    cancelCurrentUpdateTimer();
    signalStartOfPushing();

    const currentCart = getCurrentCart();

    const params = {
      order_token: orderToken,
      line_items: parseUpdateLineItems(currentCart?.line_items),
      session_hub_id: currentHub?.id,
      locale
    };

    setApiCallController(new AbortController());

    const cartLastPushedAt = Date();

    axios
      .patch(`${baseEndpoint}/${orderNumber}/cart.json`, params)
      .then(response => {
        const newFrontendCart = getCurrentCart();
        const noPendingUpdates = cartLastPushedAt > newFrontendCart?.updatedAt;

        if (noPendingUpdates) {
          const cart = response?.data?.cart;
          setCurrentCart(cart, {isLocalUpdate: false});
        } else resetUpdateTimer();

        signalEndOfPushing();
      });
  };

  const signalStartOfLoading = () => {
    setIsLoading(true);
    signalCartIsLoading();
    $broadcast("reactCart:load:started");
  };

  const signalEndOfLoading = () => {
    setIsLoading(false);
    signalCartWasLoaded();
    $broadcast("reactCart:load:finished");
  };

  const loadCart = (options = {}) => {
    if (isLoading || isPushing) return;

    signalStartOfLoading();

    const params = {
      order_token: orderToken,
      locale
    };

    if (options?.removeUnavailableItems) params.remove_unavailable_items = "t";

    axios
      .get(`${baseEndpoint}/${orderNumber}/cart.json`, {params})
      .then(response => {
        const cart = response?.data?.cart;

        setCurrentCart(cart, {isLocalUpdate: false});
        setOrderNumber(cart?.number);
        setOrderToken(cart?.token);
        signalEndOfLoading();
      });
  };

  useEffect(() => {
    if (orderNumber && orderToken) {
      loadCart();
    }
  }, [orderNumber, orderToken]);

  useEffect(() => {
    if (signalCartPush) pushCartUpdates();
  }, [signalCartPush]);

  useEffect(() => {
    if (loadRequested) loadCart(loadOptions);
  }, [loadRequested, loadOptions]);

  useEffect(() => {
    if (!orderNumber || !orderToken) return;

    const currentCart = getCurrentCart();
    if (currentCart?.locale !== locale) loadCart();
  }, [locale]);

  useEffect(() => {
    loadCart();
  }, [pageCode]);

  useEffect(() => {
    initializeOrderParams();

    // Listeners
    addEventListener("globalCartTimer", resetUpdateTimer);
    // Push changes before page unload:
    addEventListener("beforeunload", (event) => {
      if (window.cartTimer) pushCartUpdates();
    });

    return () => {
      removeEventListener("globalCartTimer", resetUpdateTimer);
    };
  }, []);

  useEffect(() => {
    setOrderNumber(window.currentOrderNumber);
    setOrderToken(window.currentOrderToken);
  }, [window.currentOrderNumber]);
};

export default useCartLifeCycle;
